import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'wouter';
import { actions } from '@/store/store.js';
import DottedSpinner from "~/components/DottedSpinner.jsx";
import { useRouter } from 'wouter';
import PromptSetForm from "~/components/PromptSetForm.jsx";
import DateTime from '~/functions/DateTime.js';
import {useDataEnv} from "~/functions/DataEnv.js";
import _ from "lodash";
import {canAccessFeature} from "~/functions/Access.js";
import redirect from "~/functions/redirect.js";

const CertProficiencyPod = ({ podule, feature, ...props }) => {
    const dataEnv = useDataEnv();
    const featurePlan = _.get(dataEnv, 'current_account.feature_plan');
    const hasAccess = canAccessFeature(featurePlan, podule);

    useEffect(() => {
        if (!hasAccess) redirect(Routes.path_path('pricing'));
    }, []);

    const dispatch = useDispatch();
    const router = useRouter();
    const route = router.base.split('/');
    const mediaFolioIdentity = route[route.length - 1];
    const certIdentity = mediaFolioIdentity.split('-').toSpliced(0, 1, 'cert').join('-');

    const proficiency = useSelector(({proficiencies}) => {
        return Object.values(proficiencies.records).find((prof) => prof.identity === podule.itself);
    });

    const certification = useSelector(({certifications }) => {
        return Object.values(certifications.records).find((cert) => cert.identity === certIdentity);
    });

    const certificationProficiency = useSelector(({ certificationProficiencies }) => {
        return Object.values(certificationProficiencies.records).find((certProf) =>
            certProf.certification_id === certification?.id && certProf.proficiency_id === proficiency?.id
        );
    });

    const trainingProfile = useSelector(({ trainingProfiles }) => trainingProfiles.currentTrainingProfile);

    const proficiencyCompletion = useSelector(({ proficiencyCompletions }) => {
        return Object.values(proficiencyCompletions.records).find((profCom) =>
            profCom.certification_proficiency_id === certificationProficiency?.id && profCom.training_profile_id === trainingProfile?.id
        );
    });

    const promptSet = useSelector(({ promptSets }) => {
        return Object.values(promptSets.records).find((promptSet) =>
            promptSet.promptable_type === 'CertificationProficiency' && promptSet.promptable_id === certificationProficiency.id
        );
    });

    const promptSetSubmissions = useSelector(({ promptSetSubmissions }) => {
        return Object.values(promptSetSubmissions.records).filter((submission) =>
            submission.responder_type === 'TrainingProfile' && submission.responder_id === trainingProfile?.id && submission.prompt_set_id === promptSet?.id
        );
    });

    useEffect(() => {
        const proficiencyIdentity = podule.itself;
        dispatch(actions.proficiencies.loadByIdentity(proficiencyIdentity));
        dispatch(actions.certifications.loadById(certIdentity));
        dispatch(actions.trainingProfiles.loadCurrentTrainingProfile());
    }, []);

    useEffect(() => {
        if (!proficiency || !certification) return;
        const certId = certification.id;
        const profId = proficiency.id;
        dispatch(actions.certificationProficiencies.loadByCertAndProf({ certId, profId }));
    }, [proficiency, certification]);

    useEffect(() => {
        if (!certificationProficiency) return;
        dispatch(actions.proficiencyCompletions.checkForCompletion({ certification_proficiency_id: certificationProficiency.id }));
    }, [certificationProficiency]);

    useEffect(() => {
        if (!certificationProficiency) return;
        dispatch(actions.promptSets.loadByPromptable({ promptable_type: 'CertificationProficiency', promptable_id: certificationProficiency.id }));
    }, [certificationProficiency]);

    useEffect(() => {
        if (!trainingProfile || !promptSet) return;
        dispatch(actions.promptSetSubmissions.query({ responder_type: 'TrainingProfile', prompt_set_id: promptSet.id }));
    }, [trainingProfile, promptSet]);

    const assessmentContainerRef = useRef(null);

    const handleSubmit = (event) => {
        dispatch(actions.proficiencyCompletions.checkForCompletion({ certification_proficiency_id: certificationProficiency.id }));
        event.preventDefault();
    };

    useEffect(() => {
        if (promptSetSubmissions?.length === 0 || !assessmentContainerRef.current) return;
        const formContainer = assessmentContainerRef.current;
        formContainer.classList.remove('show');
        formContainer.classList.add('collapse');
    }, [promptSetSubmissions]);

    if (!proficiency || !certificationProficiency) {
        return <DottedSpinner/>;
    }

    return (
        <div className="cert-prof-page">
            <h1 className="cert-prof-title">{proficiency.name}</h1>
            <div
                className="cert-prof-main"
                dangerouslySetInnerHTML={{__html: certificationProficiency?.content_template?.content_html}}
            ></div>

            <div className="site-row m-0">
                <Link to={`/`}>&lt; Back to {feature.navTitle}</Link>
            </div>

            <div className="site-row"><hr/></div>

            {!!proficiencyCompletion && (
                <div className="cert-banner completion-banner">
                    <span className="icon fa fa-medal fa-2x"></span> <span>Complete</span>
                </div>
            )}
            {!!promptSet && !proficiencyCompletion && (
                <>
                    <div className="cert-prof-assessment">
                        {promptSetSubmissions.length > 0 && (
                            <div className="cert-prof-previous-attempts mb-4">
                                {promptSetSubmissions.map((submission) => (
                                    <div key={submission.id}>
                                        Attempt {DateTime.jupiter(submission.created_at)}: <i>Not Passed</i>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="btn-wrapper">
                            <button className="post-assessment btn site-btn" type="button" data-bs-toggle="collapse"
                                    data-bs-target="#assessment-container" aria-expanded="false" aria-controls="assessment-container">
                                Start the Post-Assessment
                            </button>
                        </div>
                        <div ref={assessmentContainerRef} className="collapse" id="assessment-container">
                            <div className="card card-body">
                                <h3 className="mb-4">Section Assessment</h3>
                                <PromptSetForm
                                    prompts={promptSet.prompts}
                                    afterSubmit={handleSubmit}
                                    responderType="TrainingProfile"
                                    promptSetId={promptSet.id}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CertProficiencyPod;
